import React, { useState, useMemo } from 'react';
import { Button, Modal, Table, Collapse, Text, Box, Card, ActionIcon, Indicator, Tooltip } from '@mantine/core';
import { Portfolio, Trade } from '../../types';
import NumberDisplay from '../../components/NumberDisplay';
import BigNumber from 'bignumber.js';
import { IconDeviceDesktopAnalytics } from '@tabler/icons-react';


interface Props {
    trades: Trade[];
    portfolios: Portfolio[];
    allocations: Record<string, {
        portfolioId: string;
        qty: number;
    }[]>;
}

interface Stat {
    totalBuyQty: number;
    totalBuyValue: number;
    totalSellQty: number;
    totalSellValue: number;
}

export const PortfolioAllocationStatsModal: React.FC<Props> = ({ trades, portfolios, allocations }) => {
    const [opened, setOpened] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const stats = useMemo(() => {
        const result: Record<string, Stat & { bySymbol: Record<string, Stat> }> = {};
        portfolios.forEach(p => {
            if (!p.id) return;
            result[p.id] = { totalBuyQty: 0, totalBuyValue: 0, totalSellQty: 0, totalSellValue: 0, bySymbol: {} };
        });
        trades.forEach(trade => {
            const isBuy = trade.side === 'BUY';
            // Convert price from fils to KD (divide by 1000)
            const currency = trade.currency || "KWD";
            const priceInKD = currency === "KWD" ? new BigNumber(trade.price).dividedBy(1000) : new BigNumber(trade.price);

            if (trade.id && allocations[trade.id]) {
                trade.allocations = allocations[trade.id];
            }

            trade.allocations?.forEach(a => {
                const pid = a.portfolioId;
                if (!pid || !result[pid]) return;

                const qty = new BigNumber(a.qty);
                const tradeValue = qty.multipliedBy(priceInKD);

                if (isBuy) {
                    result[pid].totalBuyQty = new BigNumber(result[pid].totalBuyQty).plus(qty).toNumber();
                    result[pid].totalBuyValue = new BigNumber(result[pid].totalBuyValue).plus(tradeValue).toNumber();
                } else {
                    result[pid].totalSellQty = new BigNumber(result[pid].totalSellQty).plus(qty).toNumber();
                    result[pid].totalSellValue = new BigNumber(result[pid].totalSellValue).plus(tradeValue).toNumber();
                }

                const sym = trade.symbol;
                if (!result[pid].bySymbol[sym]) {
                    result[pid].bySymbol[sym] = {
                        totalBuyQty: 0,
                        totalBuyValue: 0,
                        totalSellQty: 0,
                        totalSellValue: 0
                    };
                }

                const ss = result[pid].bySymbol[sym];
                if (isBuy) {
                    ss.totalBuyQty = new BigNumber(ss.totalBuyQty).plus(qty).toNumber();
                    ss.totalBuyValue = new BigNumber(ss.totalBuyValue).plus(tradeValue).toNumber();
                } else {
                    ss.totalSellQty = new BigNumber(ss.totalSellQty).plus(qty).toNumber();
                    ss.totalSellValue = new BigNumber(ss.totalSellValue).plus(tradeValue).toNumber();
                }
            });
        });
        return result;
    }, [trades, portfolios]);

    const activePortfolios = useMemo(
        () => portfolios.filter(p => p.id && ((stats[p.id!]?.totalBuyQty || 0) > 0 || (stats[p.id!]?.totalSellQty || 0) > 0)),
        [portfolios, stats]
    );

    const toggle = (pid: string) => {
        setExpandedId(prev => (prev === pid ? null : pid));
    };

    return (
        <>
            <Indicator inline label={`${activePortfolios.length}`} disabled={activePortfolios.length === 0} color="blue" size={16}>
                <Tooltip label="View Allocations by Portfolio" withArrow>
                    <ActionIcon variant='light'
                        c={activePortfolios.length === 0 ? undefined : 'blue'}
                        onClick={() => setOpened(true)}
                        size='lg'
                        disabled={activePortfolios.length === 0}
                    >
                        <IconDeviceDesktopAnalytics size={16} />
                    </ActionIcon>
                </Tooltip>
            </Indicator>
            <Modal opened={opened} onClose={() => setOpened(false)} title="Portfolio Allocations" size="70%">
                <Box style={theme => ({ border: `1px solid ${theme.colors.gray[3]}`, borderRadius: theme.radius.sm })}>
                    <Table striped highlightOnHover verticalSpacing="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th ta="center">Portfolio</Table.Th>
                                <Table.Th ta="center">Buy Qty</Table.Th>
                                <Table.Th ta="center">Buy Value</Table.Th>
                                <Table.Th ta="center">Sell Qty</Table.Th>
                                <Table.Th ta="center">Sell Value</Table.Th>
                                <Table.Th ta="center">Net Value</Table.Th>
                                <Table.Th />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {activePortfolios.map(p => {
                                const pid = p.id!;
                                const s = stats[pid] || { totalBuyQty: 0, totalBuyValue: 0, totalSellQty: 0, totalSellValue: 0, bySymbol: {} };
                                const net = s.totalBuyValue - s.totalSellValue;
                                const isOpen = expandedId === pid;
                                return (
                                    <React.Fragment key={pid}>
                                        <Table.Tr>
                                            <Table.Td ta="center">
                                                <Box>
                                                    <Text fw={500}>{p.id}</Text>
                                                    {p.name_en && <Text size="xs" c="dimmed">{p.name_en}</Text>}
                                                </Box>
                                            </Table.Td>
                                            <Table.Td ta="center"><NumberDisplay value={s.totalBuyQty} withCommas /></Table.Td>
                                            <Table.Td ta="center"><NumberDisplay value={s.totalBuyValue} withCommas /></Table.Td>
                                            <Table.Td ta="center"><NumberDisplay value={s.totalSellQty} withCommas /></Table.Td>
                                            <Table.Td ta="center"><NumberDisplay value={s.totalSellValue} withCommas /></Table.Td>
                                            <Table.Td ta="center"><NumberDisplay value={net} withCommas /></Table.Td>
                                            <Table.Td>
                                                <Button variant="subtle" size="xs" onClick={() => toggle(pid)}
                                                //   leftIcon={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                                                >
                                                    {isOpen ? 'Hide' : 'Show'}
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td colSpan={7} p={0}>
                                                <Collapse expanded={isOpen}>
                                                    <Card style={{ width: '100%' }}>
                                                        <Table striped withTableBorder withColumnBorders ta="center">
                                                            <Table.Thead>
                                                                <Table.Tr>
                                                                    {/* <Table.Th /> */}
                                                                    <Table.Th ta="center">Symbol</Table.Th>
                                                                    <Table.Th ta="center">Buy Qty</Table.Th>
                                                                    <Table.Th ta="center">Buy Value</Table.Th>
                                                                    <Table.Th ta="center">Sell Qty</Table.Th>
                                                                    <Table.Th ta="center">Sell Value</Table.Th>
                                                                    <Table.Th ta="center">Net Value</Table.Th>
                                                                </Table.Tr>
                                                            </Table.Thead>
                                                            <Table.Tbody fz="sm">
                                                                {Object.entries(s.bySymbol).map(([sym, ss]) => {
                                                                    const netSym = ss.totalBuyValue - ss.totalSellValue;
                                                                    return (
                                                                        <Table.Tr key={sym}>
                                                                            {/* <Table.Td /> */}
                                                                            <Table.Td>{sym}</Table.Td>
                                                                            <Table.Td ta="center"><NumberDisplay value={ss.totalBuyQty} withCommas /></Table.Td>
                                                                            <Table.Td ta="center"><NumberDisplay value={ss.totalBuyValue} withCommas /></Table.Td>
                                                                            <Table.Td ta="center"><NumberDisplay value={ss.totalSellQty} withCommas /></Table.Td>
                                                                            <Table.Td ta="center"><NumberDisplay value={ss.totalSellValue} withCommas /></Table.Td>
                                                                            <Table.Td ta="center"><NumberDisplay value={netSym} withCommas /></Table.Td>
                                                                        </Table.Tr>
                                                                    );
                                                                })}
                                                                {Object.keys(s.bySymbol).length === 0 && (
                                                                    <Table.Tr>
                                                                        <Table.Td colSpan={7}>
                                                                            <Text ta="center" c="dimmed">No allocations</Text>
                                                                        </Table.Td>
                                                                    </Table.Tr>
                                                                )}
                                                            </Table.Tbody>
                                                        </Table>
                                                    </Card>
                                                </Collapse>
                                            </Table.Td>
                                        </Table.Tr>
                                    </React.Fragment>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                </Box>
            </Modal>
        </>
    );
};





// Usage example:
// <PortfolioAllocationStatsModal trades={tradesArray} portfolios={portfolioArray} />
